import type {
  ApiKeyCreated,
  ApiKeyScope,
  ApiKeySummary,
  ScimGroup,
  ScimUser,
  SsoConnection
} from "@kuroo/contracts";
import type {
  ScimGroupInput,
  ScimListResponse,
  ScimPatchOperation,
  ScimServiceProviderConfig,
  ScimTokenCreated,
  ScimTokenSummary,
  ScimUserInput
} from "./client-auth-types";

export type SdkIdentityContext = {
  baseUrl: string;
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
  requestScim: <T>(path: string, token: string, init?: RequestInit) => Promise<T>;
};

export function ssoConnections(context: SdkIdentityContext): Promise<SsoConnection[]> {
  return context.request<SsoConnection[]>("/api/v1/auth/sso/connections");
}

export function createSsoConnection(context: SdkIdentityContext, input: { name: string; protocol: "oidc" | "saml"; issuer?: string; clientId?: string; clientSecret?: string; metadataUrl?: string; authorizationUrl?: string; tokenUrl?: string; jwksUrl?: string; entityId?: string; certificatePem?: string; claimMapping?: Record<string, string>; groupRoleMapping?: Partial<Record<"admin" | "manager" | "provider" | "customer", string[]>> }): Promise<SsoConnection> {
  return context.request<SsoConnection>("/api/v1/auth/sso/connections", { method: "POST", body: JSON.stringify(input) });
}

export function updateSsoConnection(context: SdkIdentityContext, connectionId: string, input: { name?: string; issuer?: string | null; clientId?: string | null; clientSecret?: string; metadataUrl?: string | null; authorizationUrl?: string | null; tokenUrl?: string | null; jwksUrl?: string | null; entityId?: string | null; certificatePem?: string | null; status?: "active" | "disabled"; claimMapping?: Record<string, string>; groupRoleMapping?: Partial<Record<"admin" | "manager" | "provider" | "customer", string[]>> }): Promise<SsoConnection> {
  return context.request<SsoConnection>(`/api/v1/auth/sso/connections/${encodeURIComponent(connectionId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function testSsoConnection(context: SdkIdentityContext, connectionId: string): Promise<{ id: string; protocol: "oidc" | "saml"; status: "ok" | "error"; checks: Record<string, string>; error: string | null }> {
  return context.request<{ id: string; protocol: "oidc" | "saml"; status: "ok" | "error"; checks: Record<string, string>; error: string | null }>(`/api/v1/auth/sso/connections/${encodeURIComponent(connectionId)}/test`, { method: "POST", body: "{}" });
}

export function ssoStartUrl(context: SdkIdentityContext, connectionId: string, redirectUri?: string): string {
  const query = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : "";
  return `${context.baseUrl}/api/v1/auth/sso/${encodeURIComponent(connectionId)}/start${query}`;
}

export function apiKeys(context: SdkIdentityContext): Promise<ApiKeySummary[]> {
  return context.request<ApiKeySummary[]>("/api/v1/admin/api-keys");
}

export function createApiKey(context: SdkIdentityContext, input: { name: string; scopes?: ApiKeyScope[]; expiresAt?: string | null }): Promise<ApiKeyCreated> {
  return context.request<ApiKeyCreated>("/api/v1/admin/api-keys", { method: "POST", body: JSON.stringify(input) });
}

export function revokeApiKey(context: SdkIdentityContext, keyId: string): Promise<{ id: string; status: "revoked" }> {
  return context.request<{ id: string; status: "revoked" }>(`/api/v1/admin/api-keys/${encodeURIComponent(keyId)}/revoke`, { method: "POST", body: "{}" });
}

export function scimTokens(context: SdkIdentityContext): Promise<ScimTokenSummary[]> {
  return context.request<ScimTokenSummary[]>("/api/v1/admin/scim-tokens");
}

export function createScimToken(context: SdkIdentityContext, input: { name: string; scopes?: string[]; expiresAt?: string | null }): Promise<ScimTokenCreated> {
  return context.request<ScimTokenCreated>("/api/v1/admin/scim-tokens", { method: "POST", body: JSON.stringify(input) });
}

export function revokeScimToken(context: SdkIdentityContext, tokenId: string): Promise<{ id: string; status: "revoked" }> {
  return context.request<{ id: string; status: "revoked" }>(`/api/v1/admin/scim-tokens/${encodeURIComponent(tokenId)}/revoke`, { method: "POST", body: "{}" });
}

export function scimServiceProviderConfig(context: SdkIdentityContext, token: string): Promise<ScimServiceProviderConfig> {
  return context.requestScim<ScimServiceProviderConfig>("/scim/v2.0/ServiceProviderConfig", token);
}

export function scimResourceTypes(context: SdkIdentityContext, token: string): Promise<ScimListResponse<Record<string, unknown>>> {
  return context.requestScim<ScimListResponse<Record<string, unknown>>>("/scim/v2.0/ResourceTypes", token);
}

export function scimSchemas(context: SdkIdentityContext, token: string): Promise<ScimListResponse<Record<string, unknown>>> {
  return context.requestScim<ScimListResponse<Record<string, unknown>>>("/scim/v2.0/Schemas", token);
}

export function scimUsers(context: SdkIdentityContext, token: string, options: { startIndex?: number; count?: number; filter?: string } = {}): Promise<ScimListResponse<ScimUser>> {
  const query = new URLSearchParams();
  if (options.startIndex !== undefined) query.set("startIndex", String(options.startIndex));
  if (options.count !== undefined) query.set("count", String(options.count));
  if (options.filter) query.set("filter", options.filter);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return context.requestScim<ScimListResponse<ScimUser>>(`/scim/v2.0/Users${suffix}`, token);
}

export function scimUser(context: SdkIdentityContext, token: string, userId: string): Promise<ScimUser> {
  return context.requestScim<ScimUser>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token);
}

export function createScimUser(context: SdkIdentityContext, token: string, input: ScimUserInput): Promise<ScimUser> {
  const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:User"] };
  return context.requestScim<ScimUser>("/scim/v2.0/Users", token, { method: "POST", body: JSON.stringify(body) });
}

export function replaceScimUser(context: SdkIdentityContext, token: string, userId: string, input: ScimUserInput): Promise<ScimUser> {
  const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:User"] };
  return context.requestScim<ScimUser>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token, { method: "PUT", body: JSON.stringify(body) });
}

export function patchScimUser(context: SdkIdentityContext, token: string, userId: string, operations: ScimPatchOperation[]): Promise<ScimUser> {
  return context.requestScim<ScimUser>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token, {
    method: "PATCH",
    body: JSON.stringify({ schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"], Operations: operations })
  });
}

export function deleteScimUser(context: SdkIdentityContext, token: string, userId: string): Promise<void> {
  return context.requestScim<void>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token, { method: "DELETE" });
}

export function scimGroups(context: SdkIdentityContext, token: string, options: { startIndex?: number; count?: number; filter?: string } = {}): Promise<ScimListResponse<ScimGroup>> {
  const query = new URLSearchParams();
  if (options.startIndex !== undefined) query.set("startIndex", String(options.startIndex));
  if (options.count !== undefined) query.set("count", String(options.count));
  if (options.filter) query.set("filter", options.filter);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return context.requestScim<ScimListResponse<ScimGroup>>(`/scim/v2.0/Groups${suffix}`, token);
}

export function scimGroup(context: SdkIdentityContext, token: string, groupId: string): Promise<ScimGroup> {
  return context.requestScim<ScimGroup>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token);
}

export function createScimGroup(context: SdkIdentityContext, token: string, input: ScimGroupInput): Promise<ScimGroup> {
  const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:Group"] };
  return context.requestScim<ScimGroup>("/scim/v2.0/Groups", token, { method: "POST", body: JSON.stringify(body) });
}

export function replaceScimGroup(context: SdkIdentityContext, token: string, groupId: string, input: ScimGroupInput): Promise<ScimGroup> {
  const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:Group"] };
  return context.requestScim<ScimGroup>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token, { method: "PUT", body: JSON.stringify(body) });
}

export function patchScimGroup(context: SdkIdentityContext, token: string, groupId: string, operations: ScimPatchOperation[]): Promise<ScimGroup> {
  return context.requestScim<ScimGroup>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token, {
    method: "PATCH",
    body: JSON.stringify({ schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"], Operations: operations })
  });
}

export function deleteScimGroup(context: SdkIdentityContext, token: string, groupId: string): Promise<void> {
  return context.requestScim<void>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token, { method: "DELETE" });
}
